import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, Send, Building2, AlertCircle, CheckCircle, X } from 'lucide-react';

import http from "../../frameworks/basic-rest/http";

import './SurveyApp.css';
import logo from '../../assets/images/logo.png';

// Import validation configuration
import { validateStep } from './validationConfig.js';

import SurveyIntro from './SurveyIntro';
import GeneralInfo from './GeneralInfo';
import ImportanceAndBenefits from './ImportanceAndBenefits';
import LeadershipPositions from './LeadershipPositions';
import SpecializedJobs from './SpecializedJobs';
import AdministrativeJobs from './AdministrativeJobs';
import SkilledWorkers from './SkilledWorkers';
import CostsProcedures from './CostsProcedures';
import StakeholderVerification from './StakeholderVerification';

const FinalComments = ({ data, onChange, validationErrors = {} }) => {
    const renderError = (fieldName) => {
        if (validationErrors[fieldName]) {
            return (
                <div className="error-message" role="alert">
                    {validationErrors[fieldName]}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="step-content">
            <h2>الملاحظات والتوصيات</h2>

            <div className="">
                <label>يرجى التفضل بتقديم أية ملاحظات أو توصيات ترونها مناسبة:</label>
                <textarea
                    value={data.comments || ''}
                    onChange={(e) => onChange('comments', e.target.value)}
                    placeholder="ملاحظاتكم وتوصياتكم..."
                    className={`form-textarea ${validationErrors.comments ? 'error' : ''}`}
                    rows="6"
                />
                {renderError('comments')}
            </div>
        </div>
    );
};

const SurveyApp = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [showValidationAlert, setShowValidationAlert] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const STORAGE_KEY = 'survey_progress';
    const STORAGE_STEP_KEY = 'survey_current_step';
    const STORAGE_START_TIME_KEY = 'survey_start_time';

    const steps = [
        { component: SurveyIntro, title: 'مقدمة' },
        { component: GeneralInfo, title: "المعلومات العامة" },
        { component: ImportanceAndBenefits, title: "أهمية ومزايا الإفصاح" },
        { component: LeadershipPositions, title: "البيانات الشخصية المتصلة بالوظائف القيادية، والمتخصصة، والإدارية، ووظائف العمالة الماهرة" },
        { component: SpecializedJobs, title: "الوظائف التخصصية والبيانات الشخصية المطلوبة" },
        { component: AdministrativeJobs, title: "وظائف العمالة الماهرة والبيانات الشخصية المطلوبة" },
        { component: SkilledWorkers, title: "وظائف العمالة الماهرة والبيانات الشخصية المطلوبة" },
        { component: CostsProcedures, title: "التكاليف والإجراءات المرتبطة بعلمية الإفصاح" },
        { component: StakeholderVerification, title: "التحقق من البيانات الشخصية للمساهمين والملاك" },
        { component: FinalComments, title: "الملاحظات والتوصيات" }
    ];

    // Load saved progress on component mount
    useEffect(() => {
        const savedData = localStorage.getItem(STORAGE_KEY);
        const savedStep = localStorage.getItem(STORAGE_STEP_KEY);

        if (savedData) {
            try {
                const parsedData = JSON.parse(savedData);
                setFormData(parsedData);
                console.log('تم تحميل البيانات المحفوظة:', parsedData);
            } catch (error) {
                console.error('Error loading saved data:', error);
                // Clear corrupted data
                localStorage.removeItem(STORAGE_KEY);
                localStorage.removeItem(STORAGE_STEP_KEY);
            }
        }

        if (savedStep) {
            const step = parseInt(savedStep);
            if (step >= 0 && step < steps.length) {
                setCurrentStep(step);
                console.log('تم تحميل الخطوة المحفوظة:', step);
            }
        }
    }, []);

    // Save progress to localStorage whenever formData changes
    useEffect(() => {
        if (Object.keys(formData).length > 0) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
        }
    }, [formData]);

    // Save current step to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem(STORAGE_STEP_KEY, currentStep.toString());
    }, [currentStep]);

    useEffect(() => {
        if (!localStorage.getItem(STORAGE_START_TIME_KEY)) {
            localStorage.setItem(STORAGE_START_TIME_KEY, Date.now().toString());
        }
    }, []);

    // Clear saved progress
    const clearSavedProgress = () => {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_STEP_KEY);
        localStorage.removeItem(STORAGE_START_TIME_KEY);
        console.log('تم مسح البيانات المحفوظة');
    };

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, [currentStep]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Clear validation error for this field when user starts typing/selecting
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }

        // Hide validation alert if no more errors
        if (showValidationAlert && Object.keys(validationErrors).length <= 1) {
            setShowValidationAlert(false);
        }
    };

    const handleNext = () => {
        // Validate current step using centralized validation
        const validation = validateStep(currentStep, formData);

        if (!validation.isValid) {
            setValidationErrors(validation.errors);
            setShowValidationAlert(true);

            // Scroll to first error field
            setTimeout(() => {
                const firstErrorField = Object.keys(validation.errors)[0];

                // Try multiple selectors to find the field (in priority order)
                let errorElement =
                    // 1. Try finding parent container with data-field attribute (best for radio/checkbox groups)
                    document.querySelector(`[data-field="${firstErrorField}"]`) ||
                    // 2. Try finding by name attribute (for text inputs)
                    document.querySelector(`[name="${firstErrorField}"]`) ||
                    // 3. Try finding by id
                    document.getElementById(firstErrorField) ||
                    // 4. Try finding the first radio/checkbox with this name
                    document.querySelector(`input[name="${firstErrorField}"]`);

                if (errorElement) {
                    // If we found a radio/checkbox input, scroll to its parent container
                    const container = errorElement.closest('.data-category') ||
                        errorElement.closest('.form-group') ||
                        errorElement.parentElement ||
                        errorElement;

                    container.scrollIntoView({ behavior: 'smooth', block: 'center' });

                    // Only try to focus if it's a focusable element (text input, textarea, select)
                    if (errorElement.tagName === 'INPUT' && errorElement.type === 'text' ||
                        errorElement.tagName === 'TEXTAREA' ||
                        errorElement.tagName === 'SELECT') {
                        errorElement.focus();
                    }
                }
            }, 100);
            return;
        }

        // Clear validation state and move to next step
        setValidationErrors({});
        setShowValidationAlert(false);

        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrevious = () => {
        // Clear validation when going back
        setValidationErrors({});
        setShowValidationAlert(false);

        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSubmit = async () => {
        const validation = validateStep(currentStep, formData);

        if (!validation.isValid) {
            setValidationErrors(validation.errors);
            setShowValidationAlert(true);
            return;
        }

        setIsSubmitting(true);
        try {
            const submissionData = {
                ...formData,
                metadata: {
                    submittedAt: new Date().toISOString(),
                    completionTime: calculateCompletionTime(),
                }
            };

            const response = await http.post('/survey', submissionData);

            console.log('Survey submitted successfully:', response.data);

            // Clear saved progress
            clearSavedProgress();
            setFormData({});
            setCurrentStep(0);
            setValidationErrors({});
            setShowValidationAlert(false);

            // Show success modal
            setShowSuccessModal(true);

        } catch (error) {
            console.error('Submit error:', error);

            if (error.response) {
                alert(`خطأ في الإرسال: ${error.response.data.message || 'فشل في إرسال البيانات'}`);
            } else if (error.request) {
                alert('فشل الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت.');
            } else {
                alert('حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Debug function to show current form data (remove in production)
    const debugFormData = () => {
        console.log('Current Form Data:', formData);
        console.log('Current Step:', currentStep);
        console.log('Validation Errors:', validationErrors);
    };

    const CurrentStepComponent = steps[currentStep].component;
    const progress = ((currentStep + 1) / steps.length) * 100;

    const SuccessModal = ({ isOpen, onClose }) => {
        if (!isOpen) return null;

        return (
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()} style={{backgroundColor: 'white'}}>
                    <button className="modal-close" onClick={onClose} aria-label="إغلاق">
                        <X size={24} />
                    </button>

                    <div className="modal-icon">
                        <CheckCircle size={64} className="success-icon" />
                    </div>

                    <h2>شكراً لمشاركتكم!</h2>

                    <div className="modal-body">
                        <p className="modal-message">
                            تم استلام ردودكم بنجاح. نقدر وقتكم واهتمامكم في المشاركة في هذه الاستبانة.
                        </p>
                        <p className="modal-submessage">
                            ستساعدنا إجاباتكم في تحسين فهمنا لاحتياجات القطاع الخاص وتطوير السياسات ذات الصلة.
                        </p>
                    </div>

                    <button className="btn btn-primary modal-button" onClick={onClose}>
                        حسناً
                    </button>
                </div>
            </div>
        );
    };

    const calculateCompletionTime = () => {
        const startTime = localStorage.getItem(STORAGE_START_TIME_KEY);
        if (startTime) {
            return Math.floor((Date.now() - parseInt(startTime)) / 1000); // in seconds
        }
        return null;
    };

    return (
        <div className="survey-app">
            <header className="survey-header">
                <div className="header-content">
                    <div className="logo-section">
                        <img src={logo} alt="بيان" className="header-logo" />
                    </div>
                </div>
            </header>

            <main className="survey-main">
                <div className="survey-container">
                    <div className="progress-section">
                        {/* Uncomment to show progress bar */}
                        {/* <div className="progress-bar">
                            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                        </div> */}
                        <span className="progress-text">
                            الخطوة {currentStep + 1} من {steps.length}
                        </span>
                    </div>

                    {/* Validation Alert */}
                    {showValidationAlert && Object.keys(validationErrors).length > 0 && (
                        <div className="validation-alert" role="alert">
                            <AlertCircle size={20} />
                            <div className="alert-content">
                                <strong>يرجى تعبئة الحقول المطلوبة</strong>
                                <p>هناك {Object.keys(validationErrors).length} حقل مطلوب لم يتم تعبئته</p>
                            </div>
                        </div>
                    )}

                    <CurrentStepComponent
                        data={formData}
                        onChange={handleInputChange}
                        validationErrors={validationErrors}
                    />

                    <div className="navigation-buttons">
                        <button
                            onClick={handlePrevious}
                            disabled={currentStep === 0}
                            className="btn btn-secondary"
                        >
                            <ChevronRight size={20} />
                            السابق
                        </button>

                        {currentStep === steps.length - 1 ? (
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className="btn btn-primary"
                            >
                                {isSubmitting ? 'جاري الإرسال...' : (
                                    <>
                                        <Send size={20} />
                                        إرسال الاستبانة
                                    </>
                                )}
                            </button>
                        ) : (
                            <button
                                onClick={handleNext}
                                className="btn btn-primary"
                            >
                                <ChevronLeft size={20} />
                                التالي
                            </button>
                        )}
                    </div>
                </div>
            </main>

            <footer className="nf-root" dir="rtl">
                <div className="nf-container">
                    <div className="nf-bottom">
                        <div className="contact-info">
                            {/* <h4>للاستفسار أو لمزيد من المعلومات</h4>
                            <p>أ.د. عبد الرحمن بن عبد العزيز حماد البواردي</p> */}
                            <p>البريد الإلكتروني</p><p> info@byaan.com</p>
                        </div>
                        <p className="nf-copy">
                            جميع الحقوق محفوظة لبيان ©، المملكة العربية السعودية، 2025 م
                        </p>
                    </div>
                </div>
                {/* Success Modal */}
                <SuccessModal
                    isOpen={showSuccessModal}
                    onClose={() => setShowSuccessModal(false)}
                />
            </footer>
        </div>
    );
};

export default SurveyApp;